/**
 * Multi-line expense reports (R-P8, `KEKA_VS_REIMBURSEMENT.md` gap row 25) — "one report holding
 * many expenses". Named `expense-multiline-report` on purpose, not `expense-report`: that file
 * name is already taken by `expense-report.contract.ts`, the unrelated read-only dashboard/CSV
 * feature (spend-by-category, budget-vs-actual). Two different "expense report" concepts, kept
 * apart at the file-name/contract-key level so they never collide — see that other file's own
 * header for what it covers.
 *
 * THE CONTAINER, NOT A REPLACEMENT FOR `ExpenseClaim`. Every real line is still a first-class
 * `expenseClaimSchema` row — its own category, GST split, mileage/per-diem, custom attributes, and
 * policy checks, created and edited through `expense.contract.ts`'s existing `createClaim`/
 * `updateClaim` with `reportId` set. This contract only adds the ONE submit/decide/withdraw wrapper
 * around a set of them. Nothing here reimplements anything R-P2 through R-P6 already built.
 *
 * ONE WORKFLOW INSTANCE PER REPORT, NOT PER CATEGORY. "Approved per category" means the single
 * `approveReport` decision can set a different `ExpenseClaim.approvedAmount` for each line/
 * category in one call (`lines` below) — it does not mean each category gets its own approval
 * chain. A true per-category chain needs the workflow team's own editor (`KEKA_VS_REIMBURSEMENT.md`
 * §5) — out of scope here, same carve-out R-P3's routing context already made for approval chains
 * generally (invariant 6: one engine, not a second one hand-rolled per feature).
 *
 * PAYOUT IS NOT A NEW MECHANISM. A report's lines are ordinary `ExpenseClaim` rows once approved,
 * so `createPaymentBatch`/`settleClaim` (already in `expense.contract.ts`) sweep them up exactly
 * like any standalone claim — "paid as one payout" is a grouping/query concern, not a second
 * settlement path.
 *
 * CONTRACT ONLY (invariant 8). Nothing in this file is implemented yet — no route, no schema
 * change, no screen may consume it until this PR lands on its own.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  decimalStringSchema,
  errorBodySchema,
  expenseClaimStatusSchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  payoutModeSchema,
  uuidSchema,
} from '../schemas/common.js';
import { expenseClaimSchema } from './expense.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

/** Mirrors `ExpenseClaim.status` exactly (draft/pending/approved/rejected/settled/withdrawn) — one
 * mental model across every reimbursement entity, not a bespoke lifecycle for the report. */
export const expenseReportStatusSchema = expenseClaimStatusSchema;

export const expenseReportSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  reference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  title: z.string(),
  status: expenseReportStatusSchema,
  /** Deliberately not a foreign key — same F12 reasoning as `ExpenseClaim.workflowInstanceId`. */
  workflowInstanceId: uuidSchema.nullable(),
  currency: currencySchema,
  /** The sum of every line's `amount`, in `currency` — set at submit. Null on a draft still being
   * assembled. */
  totalAmount: decimalStringSchema.nullable(),
  /** The sum of every line's `approvedAmount` (falling back to its own `amount` where an approver
   * left a line untouched) — set once the report's one decision is made. Null until then. */
  totalApprovedAmount: decimalStringSchema.nullable(),
  /** How many `ExpenseClaim` rows currently carry this report's id — for the list view, so the
   * client is not forced to fetch every line just to show a count. */
  lineCount: z.number().int().nonnegative(),
  submittedAt: instantSchema.nullable(),
  payoutMode: payoutModeSchema.nullable(),
  payoutReference: z.string().nullable(),
  settledAt: instantSchema.nullable(),
  settledById: uuidSchema.nullable(),
  settledByName: z.string().nullable(),
  amountInBaseCurrency: decimalStringSchema.nullable(),
  paymentBatchId: uuidSchema.nullable(),
  advanceId: uuidSchema.nullable(),
  advanceReference: z.string().nullable(),
  advanceAppliedAmount: decimalStringSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});

export type ExpenseReport = z.infer<typeof expenseReportSchema>;

/** `getReport` alone embeds the actual lines — every list response stays light (`lineCount` only),
 * consistent with how `listClaims` never embeds attachments either. */
export const expenseReportDetailSchema = expenseReportSchema.extend({
  claims: z.array(expenseClaimSchema),
});

export type ExpenseReportDetail = z.infer<typeof expenseReportDetailSchema>;

export const listExpenseReportsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller's own reports. Anyone else's requires `expense:read` over them. */
  personId: uuidSchema.optional(),
  status: expenseReportStatusSchema.optional(),
  /** Free text over reference and title. */
  q: z.string().trim().min(1).max(120).optional(),
});

export const createExpenseReportBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  currency: currencySchema,
});

export const updateExpenseReportBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
});

export const withdrawExpenseReportBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

/** One override per line, keyed by claim id — how "approved per category" is expressed in a
 * single decision. Absent for a line means "approve at the claimed amount", same convention
 * `decideExpenseClaimBodySchema.approvedAmount` already uses one level down. Ignored entirely on
 * `rejectReport` (a rejection settles nothing), same relationship `decideExpenseClaimBodySchema`
 * already has with `approveClaim` vs `rejectClaim`. */
export const decideExpenseReportLineSchema = z.object({
  claimId: uuidSchema,
  approvedAmount: decimalStringSchema.optional(),
});

export const decideExpenseReportBodySchema = z.object({
  comment: z.string().max(500).optional(),
  lines: z.array(decideExpenseReportLineSchema).optional(),
});

export const expenseMultilineReportContract = c.router(
  {
    listReports: {
      method: 'GET',
      path: '/',
      query: listExpenseReportsQuerySchema,
      responses: {
        200: paginatedSchema(expenseReportSchema),
        401: errorBodySchema,
      },
      summary: 'List multi-line expense reports in the callers visible scope',
      metadata: routeMeta('expense:read'),
    },

    /** Mirrors `expense.contract.ts`'s own `listApprovals` exactly — every report with a
     * `WorkflowStep` currently awaiting THIS caller's decision, across everyone they approve for,
     * plus (for anyone holding `workflow:manage` org-wide) every OTHER person's `pending`/
     * `rejected` report, visibility-only, same reasoning as that route's own header comment. */
    listReportApprovals: {
      method: 'GET',
      path: '/approvals',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(expenseReportSchema),
        401: errorBodySchema,
      },
      summary: "List multi-line reports with a step currently awaiting the caller's decision",
      metadata: routeMeta(null),
    },

    getReport: {
      method: 'GET',
      path: '/:expenseReportId',
      responses: {
        200: expenseReportDetailSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One report with every line claim currently tagged to it',
      metadata: routeMeta('expense:read'),
    },

    createReport: {
      method: 'POST',
      path: '/',
      body: createExpenseReportBodySchema,
      responses: {
        200: expenseReportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Start a new draft report — line claims are added afterward via createClaim',
      metadata: routeMeta('expense:submit'),
    },

    updateReport: {
      method: 'PATCH',
      path: '/:expenseReportId',
      body: updateExpenseReportBodySchema,
      responses: {
        200: expenseReportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not `draft` — already submitted. */
        409: errorBodySchema,
      },
      summary: "Rename one of your own draft reports",
      metadata: routeMeta('expense:submit'),
    },

    deleteReport: {
      method: 'DELETE',
      path: '/:expenseReportId',
      responses: {
        200: expenseReportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete one of your own draft reports (its line claims are orphaned, not deleted)',
      metadata: routeMeta('expense:submit'),
    },

    submitReport: {
      method: 'POST',
      path: '/:expenseReportId/submit',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: expenseReportDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** No line claims, a line not in the report's own currency, or a line still in draft. */
        422: errorBodySchema,
      },
      summary: 'Submit a draft report and start its approval workflow',
      metadata: routeMeta('expense:submit'),
    },

    withdrawReport: {
      method: 'POST',
      path: '/:expenseReportId/withdraw',
      headers: idempotencyHeadersSchema,
      body: withdrawExpenseReportBodySchema,
      responses: {
        200: expenseReportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Withdraw a draft or pending report before it is decided',
      metadata: routeMeta('expense:submit'),
    },

    approveReport: {
      method: 'POST',
      path: '/:expenseReportId/approve',
      headers: idempotencyHeadersSchema,
      body: decideExpenseReportBodySchema,
      responses: {
        200: expenseReportDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** No workflow instance, or no step currently awaiting a decision. */
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller, per line if lines is given',
      metadata: routeMeta(null),
    },

    rejectReport: {
      method: 'POST',
      path: '/:expenseReportId/reject',
      headers: idempotencyHeadersSchema,
      body: decideExpenseReportBodySchema,
      responses: {
        200: expenseReportDetailSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/expense/multiline-reports' },
);
